import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CategoriesSubnav from "@/components/CategoriesSubnav";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { ArrowRight, CircleCheck } from "lucide-react";

const PRODUCT_SLUGS = [
  "carton-standard-renforce",
  "carton-a-livres",
  "carton-75-verres",
  "carton-100-verres",
  "carton-24-assiettes",
  "carton-12-bouteilles",
  "carton-penderie-petit",
  "carton-penderie-grand",
  "conteneur-boites-archives",
  "carton-informatique-grand",
  "carton-informatique-petit",
];

const CartonsDemenagement = () => {
  useDocumentTitle(
    "Cartons de déménagement Paris Île-de-France | Cartons solides et spécialisés",
    "Achetez vos cartons de déménagement à Paris et en Île-de-France : carton standard renforcé, carton livres, carton penderie, carton bouteille, carton informatique et conteneur archives."
  );

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const cartonsFiltered = PRODUCT_SLUGS.map((s) => products.find((p) => p.slug === s)).filter(Boolean);

  return (
    <div data-testid="cartons-demenagement-page">
      <CategoriesSubnav />

      {/* TOP — H1 + grid + intro */}
      <section className="section-pad bg-white" id="produits">
        <div className="container-cdm">
          <div className="max-w-3xl mx-auto text-center">
            <div className="overline justify-center">Boutique cartonmove</div>
            <h1 className="mt-3 text-[28px] sm:text-[33px] md:text-[48px] font-extrabold text-[#0f1d2b] leading-tight">
              Cartons de déménagement
            </h1>
          </div>

          {loading ? (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-square bg-zinc-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="cartons-grid">
              {cartonsFiltered.map((p, idx) => (
                <ProductCard key={p.id} product={p} index={idx} hideQty ctaLabel="Acheter" />
              ))}
            </div>
          )}

          <div className="mt-12 max-w-3xl mx-auto text-center">
            <p className="text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Retrouvez tous nos cartons de déménagement pour emballer, protéger et transporter vos
              affaires&nbsp;: cartons standards, cartons livres, cartons penderie, cartons bouteilles,
              cartons informatiques et conteneurs archives. Des cartons solides et pratiques pour
              préparer un déménagement à Paris, en Île-de-France, en appartement, maison ou bureau.
            </p>
          </div>

          {/* CTA Big Déménagement — identique à la home */}
          <div className="mt-12">
            <BigDemenageurCTA testId="cartons-bigdem-cta" />
          </div>

          {/* LIVRAISON IDF — bloc dédié */}
          <div className="mt-12 max-w-4xl mx-auto bg-white border border-[#e6edf3] rounded-2xl p-6 sm:p-8 lg:p-10">
            <div className="overline">Service de livraison</div>
            <h2 className="mt-3 text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Livraison en Île-de-France
            </h2>
            <p className="mt-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Nous livrons directement vos cartons et fournitures de déménagement avec notre propre
              société, uniquement en Île-de-France.
            </p>
            <p className="mt-3 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Pour vous éviter d'attendre toute la journée, nous convenons ensemble d'un créneau de
              livraison adapté à vos disponibilités. Vous recevez votre commande à l'heure prévue,
              simplement et sans perte de temps.
            </p>

            <h3 className="mt-6 text-base font-extrabold text-[#0f1d2b]">Points forts :</h3>
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5" data-testid="livraison-highlights">
              {[
                "Livraison par notre propre équipe",
                "Service disponible en Île-de-France",
                "Créneau horaire défini avec le client",
                "Cartons livrés directement à domicile ou en entreprise",
              ].map((h) => (
                <li key={h} className="flex items-start gap-2.5 text-[15px] text-[#0f1d2b] leading-relaxed">
                  <CircleCheck className="h-5 w-5 mt-0.5 shrink-0 text-[#237cb5]" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SEO H2 sections */}
      <section className="section-pad bg-[#EEF3F7] border-t border-[#e6edf3]">
        <div className="container-cdm max-w-4xl space-y-10">
          <article>
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Quel carton choisir pour un déménagement&nbsp;?
            </h2>
            <p className="mt-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Tous les cartons ne servent pas au même usage. Le <strong>carton standard renforcé</strong>{" "}
              convient aux objets du quotidien, le <strong>carton livres</strong> est adapté aux
              charges lourdes, le <strong>carton penderie</strong> permet de transporter les vêtements
              sur cintres, et le <strong>carton bouteille</strong> protège les bouteilles pendant le
              transport. Pour un déménagement bien organisé, il est conseillé de varier les formats
              selon les affaires à emballer.
            </p>
          </article>

          <article>
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Cartons pour appartement, maison et bureau
            </h2>
            <p className="mt-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Nos cartons de déménagement sont adaptés aux studios, appartements, maisons et transferts
              de bureaux. Pour un petit logement, quelques cartons standards et cartons livres peuvent
              suffire. Pour un appartement familial ou une maison, il est préférable de prévoir
              plusieurs formats&nbsp;: cartons renforcés, penderies, cartons spécialisés et conteneurs
              archives pour les documents.
            </p>
          </article>

          <article>
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Cartons de déménagement pour Paris et l'Île-de-France
            </h2>
            <p className="mt-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              À Paris et en Île-de-France, un déménagement demande souvent des cartons pratiques à
              porter, résistants et faciles à empiler. Entre les escaliers, les ascenseurs, les caves,
              les rues étroites et les trajets en camion, il est important d'utiliser des cartons
              adaptés au transport et à la manutention.
            </p>
          </article>

          <article>
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Combien de cartons prévoir pour déménager&nbsp;?
            </h2>
            <div className="mt-4 overflow-hidden rounded-xl border border-[#e6edf3] bg-white">
              <table className="w-full text-[14px] sm:text-[15px]">
                <thead className="bg-[#f7fafc] text-[#0f1d2b]">
                  <tr>
                    <th className="text-left px-4 py-3 font-extrabold">Type de logement</th>
                    <th className="text-left px-4 py-3 font-extrabold">Nombre indicatif de cartons</th>
                  </tr>
                </thead>
                <tbody className="text-[#3a4a5a]">
                  {[
                    ["Studio", "10 à 20 cartons"],
                    ["T2", "20 à 35 cartons"],
                    ["T3", "35 à 55 cartons"],
                    ["Maison", "60 cartons et plus"],
                    ["Bureau", "Selon le nombre de postes et d'archives"],
                  ].map(([k, v]) => (
                    <tr key={k} className="border-t border-[#e6edf3]">
                      <td className="px-4 py-3 font-semibold text-[#0f1d2b]">{k}</td>
                      <td className="px-4 py-3">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>

          <article>
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Cartons spécialisés pour objets fragiles ou spécifiques
            </h2>
            <p className="mt-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Certains objets demandent un emballage particulier. Le{" "}
              <strong>carton bouteille avec alvéoles</strong> protège les bouteilles, les{" "}
              <strong>cartons informatiques</strong> sont adaptés aux équipements de bureau, et les{" "}
              <strong>cartons penderie</strong> permettent de transporter les vêtements sans les
              plier. Ces cartons spécialisés aident à limiter les risques de casse, de déformation
              ou de désorganisation pendant le déménagement.
            </p>
          </article>

          {/* FAQ */}
          <article>
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              FAQ cartons de déménagement
            </h2>
            <div className="mt-5 space-y-5">
              {[
                {
                  q: "Quel carton choisir pour les livres ?",
                  a: "Le carton livres est conseillé, car son format plus compact évite de créer des cartons trop lourds à porter.",
                },
                {
                  q: "Quel carton utiliser pour les vêtements ?",
                  a: "Pour les vêtements pliés, un carton standard peut suffire. Pour les vêtements sur cintres, le carton penderie est plus adapté.",
                },
                {
                  q: "Quel carton choisir pour les bouteilles ?",
                  a: "Le carton bouteille 12 alvéoles permet de séparer et protéger les bouteilles pendant le transport.",
                },
                {
                  q: "Les cartons sont-ils adaptés aux déménagements professionnels ?",
                  a: "Oui, certains formats comme le conteneur archives et les cartons informatiques sont adaptés aux bureaux, documents et matériel professionnel.",
                },
              ].map(({ q, a }) => (
                <div key={q} className="bg-white rounded-xl border border-[#e6edf3] p-5">
                  <h3 className="flex items-start gap-2 text-[15px] sm:text-base font-extrabold text-[#0f1d2b]">
                    <CircleCheck className="h-5 w-5 mt-0.5 shrink-0 text-[#237cb5]" />
                    {q}
                  </h3>
                  <p className="mt-2 ml-7 text-[14.5px] text-[#3a4a5a] leading-[1.8]">{a}</p>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default CartonsDemenagement;
